import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'
import NormalLayoutContainer from '../../../../shared/NormalLayoutContainer'
import { PageTitle } from '../../../../../components/page-title'
import Error from '../../../../shared/Error'
import { Spinner } from '../../../../shared/Spinner'
import FormExplanation from '../../../../shared/FormExplanation'
import SiteTypeStatusLabel from '../../../../shared/SiteTypeStatusLabel'
import useQuery from '../../../../../hooks/useQuery'
import QUERY_KEYS from '../../../../../graphql/queryKeys'
import { captureErrorAndNotify } from '../../../../../utilities/errorHandlers'
import siteTypesQuery from '../../../../../graphql/queries/siteTypes'
import EditSiteTypeForm from './components/EditSiteTypeForm'
import updateSiteType from '../../../../../graphql/mutations/updateSiteType'
import handleError from '../../../../../utilities/handleError'
import useMutation from '../../../../../hooks/useMutation'
import {
  CLEAR_CURRENT_SITE_TYPE_ERRORS,
  CLEAR_SITE_TYPES_DISABLE_ERRORS,
  CLEAR_SITE_TYPES_ENABLE_ERRORS,
  RECEIVE_UPDATE_CURRENT_SITE_TYPE_FAILURE,
  RECEIVE_UPDATE_CURRENT_SITE_TYPE_SUCCESS
} from '../../action/siteTypeAction'
import notify from '../../../../../utilities/notify'

// eslint-disable-next-line camelcase
export default function HaulerSiteTypes_IdEditContainer () {
  const dispatch = useDispatch()
  const { user } = useSelector(({ user: { user, hauler } }) => ({ user, hauler }))
  const history = useHistory()
  const { id } = useParams()
  const [formValues, setFormValues] = useState({
    name: '',
    shortCode: ''
  })

  const { siteTypesDisable, siteTypesEnable } =
    useSelector(({ siteTypesDisable, siteTypesEnable }) => ({
      siteTypesDisable, siteTypesEnable
    }))

  const cancelEditSiteType = () => {
    history.push('/hauler/site-types')
  }

  const clearCurrentSiteTypeErrors = () => {
    dispatch({ type: CLEAR_CURRENT_SITE_TYPE_ERRORS, payload: {} })
  }

  const clearSiteTypesEnableErrors = () => {
    dispatch({ type: CLEAR_SITE_TYPES_ENABLE_ERRORS, payload: {} })
  }

  const clearSiteTypesDisableErrors = () => {
    dispatch({ type: CLEAR_SITE_TYPES_DISABLE_ERRORS, payload: {} })
  }

  const { mutateAsync: updateSiteTypeMutation } = useMutation(updateSiteType, {
    onSuccess ({ siteType }) {
      dispatch({ type: RECEIVE_UPDATE_CURRENT_SITE_TYPE_SUCCESS, payload: { siteType } })
      notify('success', `Successfully updated Site Type ${siteType.shortCode}`)
      history.push('/hauler/site-types')
    },
    onError (error) {
      handleError(error)
      dispatch({ type: RECEIVE_UPDATE_CURRENT_SITE_TYPE_FAILURE, payload: { errors: error } })
    }
  })

  const enableSiteType = () => {
    updateSiteTypeMutation({
      name: siteTypeData.name,
      shortCode: siteTypeData.shortCode,
      siteTypeId: id,
      status: 'enabled'
    })
  }

  const disableSiteType = () => {
    updateSiteTypeMutation({
      name: siteTypeData.name,
      shortCode: siteTypeData.shortCode,
      siteTypeId: id,
      status: 'disabled'
    })
  }

  const handleFormSubmit = (e) => {
    e.preventDefault()
    updateSiteTypeMutation({
      name: formValues.name,
      shortCode: formValues.shortCode,
      siteTypeId: id,
      status: siteTypeData.status
    })
  }

  const { data: currentSiteType, isLoading: currentSiteTypeLoading } = useQuery(
    [
      QUERY_KEYS.siteTypes,
      user.haulerId,
      [id]
    ],
    siteTypesQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve site type')
      }
    }
  )

  useEffect(() => {
    const siteTypeData = currentSiteType?.siteTypes[0]
    if (siteTypeData?.name && siteTypeData?.shortCode) {
      setFormValues({
        name: siteTypeData.name,
        shortCode: siteTypeData.shortCode
      })
    }
  }, [currentSiteType])

  if (currentSiteTypeLoading) return <Spinner isFetching />

  const siteTypeData = currentSiteType.siteTypes[0]

  return (
    <div>
      <NormalLayoutContainer showBackLink>
        <div className='container-fluid'>
          <PageTitle title='Edit Existing Site Type'>
            <button
              className='btn btn-link pull-right push-down-super-small'
              onClick={cancelEditSiteType}>Cancel
            </button>
          </PageTitle>
          <div>
            <div className='row'>
              <div className='col-xs-12 col-sm-12 col-md-7 col-lg-7'>

                <h4>Change Status</h4>
                <div className='row panel panel-default padding-10px'>
                  <div className='font-size-1-5em inline-block'>
                    Current Status:
                    <span className='space-left'>
                      <SiteTypeStatusLabel status={siteTypeData.status} />
                    </span>
                  </div>

                  <ReactTooltip />
                  {/* Enable/Disable Button */}
                  {(() => {
                    if (siteTypeData.status === 'enabled') {
                      const disallowDisable = siteTypeData.sitesCount && siteTypeData.sitesCount > 0
                      return (<div
                        className='inline-block pull-right margin-top-0-5-pct'
                        data-tip={disallowDisable ? "Can't disable with attached sites" : null}>
                        <button
                          className='btn btn-danger btn-sm'
                          onClick={() => disableSiteType()}
                          disabled={disallowDisable}>
                          <strong>Disable</strong>
                        </button>
                      </div>)
                    } else if (siteTypeData.status === 'disabled') {
                      return (<button
                        className='btn btn-primary btn-sm inline-block pull-right margin-top-0-5-pct'
                        onClick={() => enableSiteType()}>
                        Enable
                      </button>)
                    }
                    return null
                  })()}
                </div>

                <h4>Change Name and Short Code</h4>
                <EditSiteTypeForm
                  onSubmit={handleFormSubmit}
                  isFetching={currentSiteTypeLoading}
                  formValues={formValues}
                  setFormValues={setFormValues}
                  resErrors={[]} // TODO: actually send the errors over on submit/rethink how these errors are done because right now it's hecking silly
                  siteType={siteTypeData ?? {}}
                  clearErrors={clearCurrentSiteTypeErrors}
                />
              </div>
              <div className='col-xs-12 col-sm-12 col-md-4 col-md-offset-1 col-lg-4 col-lg-offset-1'>
                <div className='row'>
                  <FormExplanation
                    messages={[
                      'You can edit the visible name of an existing Site Types on this page.',
                      'Site Types are meant to be types of your preset locations, like Container Yards, Transfer Stations, and Repair Shops.',
                      'Note that editing the name of a asset type here changes the name of that Site Type across the system.'
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </NormalLayoutContainer>
      <Error errors={siteTypesEnable.errors} clearErrors={clearSiteTypesEnableErrors} />
      <Error errors={siteTypesDisable.errors} clearErrors={clearSiteTypesDisableErrors} />
    </div>
  )
}
